import { MutableRefObject, useEffect, useState } from "react";
import { mouseWheelHandler } from "../_handlers/mouseWheel";
import {
    IPerfectScrollContext,
    IPerfectScrollExtensions,
    IPerfectScrollOptions,
} from "./perfectScrollModel";
import { buildCustomScrollStore } from "../_store/customScrollStore";
import { isObjectPropsEquals } from "../_utils/equality";
import { useSetOffsetsOnScrollFeature } from "../_features/setOffsetsOnScroll/setOffsetsOnScrollFeatureHook";
import { callAllFns, noopFn } from "../_utils/fnUtils";
import { useHorizontalNegativeScrollAdjustment } from "../_features/horizontalNegativeScrollAdjustment/horizontalNegativeScrollAdjustmentHook";
import { useShowScrollBarsOnDemandFeature } from "../_features/showScrollBarsOnDemand/showScrollBarsOnDemandFeatureHook";
import {
    useRecalculateDimensionsOnInternalChange,
    useRecalculateDimensionsOnResize,
} from "../_features/recalculateDimensions/recalculateDimensionsFeatureHooks";
import { useMouseWheelHandler } from "../_handlers/touch";
import { useRecalculateDimensionsOnAnimationFrameFeature } from "../_features/recalculateDimensions/recalculateDimensionsOnAnimationFrameFeatureHooks";
import { useCustomScrollObjectSelector } from "../_store/customScrollSelectorHook";
import { optionsSelector } from "../_shared/optionsSelector";

const useFeatures = callAllFns(
    useSetOffsetsOnScrollFeature,
    useHorizontalNegativeScrollAdjustment,
    useShowScrollBarsOnDemandFeature,
    useRecalculateDimensionsOnResize,
    useRecalculateDimensionsOnInternalChange,
    useRecalculateDimensionsOnAnimationFrameFeature
);

const defaultSettings: IPerfectScrollOptions = {
    useBothWheelAxes: false,
    wheelSpeed: 1,
    wheelPropagation: true,
    useAnimationFrame: true,
};

export function usePerfectScroll(
    containerRef: MutableRefObject<HTMLElement>,
    settings: IPerfectScrollOptions = defaultSettings,
    extensions = {} as IPerfectScrollExtensions
): [IPerfectScrollContext] {
    const [customScrollContext] = useState<IPerfectScrollContext>(() => ({
        store: buildCustomScrollStore(false),
    }));

    useEffect(
        () =>
            containerRef.current
                ? mouseWheelHandler(
                      containerRef.current,
                      customScrollContext.store
                  )
                : noopFn,
        [containerRef.current]
    );

    useMouseWheelHandler(containerRef.current, customScrollContext.store);

    const shouldSetUpOptions =
        settings &&
        !isObjectPropsEquals(
            settings,
            useCustomScrollObjectSelector(optionsSelector, customScrollContext)
        );
    useEffect(() => {
        shouldSetUpOptions &&
            customScrollContext.store.dispatch({
                type: "setOptions",
                options: settings,
            });
    }, [shouldSetUpOptions]);

    useFeatures(containerRef, customScrollContext.store, extensions);

    return [customScrollContext];
}
